import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  IconButton,
  Drawer,
  Tooltip,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Menu as MenuIcon,
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
  NotificationsActive as BellIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getUserDepartmentComponents } from '../BaseURL';

const PRIMARY = '#1A9B7F';
const FULL_ACCESS_ROLES = ['Head Of Assembly', 'System Administrator'];

const DRAWER_WIDTH = 264;
const RAIL_WIDTH = 68;
const TRANSITION = 'cubic-bezier(0.4, 0, 0.2, 1)';
const SIDEBAR_MODE_KEY = 'totalcare:departmentSidebarMode';

/**
 * Shared department panel layout — collapsible sidebar.
 *
 * Sidebar has two modes (persisted in localStorage):
 *   - 'fixed':    always full width (264px)
 *   - 'floating': collapses to a 68px icon rail; expands on hover
 *
 * Props:
 *   - departmentId    (number)  used by /getUserDepartmentComponents for role gating
 *   - departmentName  (string)  shown in the sidebar header
 *   - departmentIcon  (node)    icon for the header
 *   - menuItems       (array)   [{ key, label, icon, component }]
 */
export default function DepartmentLayout({
  departmentId,
  departmentName,
  departmentIcon,
  menuItems = [],
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [allowedKeys, setAllowedKeys] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sidebar collapse state
  const [sidebarMode, setSidebarMode] = useState(
    () => localStorage.getItem(SIDEBAR_MODE_KEY) || 'fixed'
  );
  const [hovering, setHovering] = useState(false);
  const isFixed = sidebarMode === 'fixed';
  const isExpanded = isFixed || hovering;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const toggleMode = () => {
    const next = isFixed ? 'floating' : 'fixed';
    setSidebarMode(next);
    localStorage.setItem(SIDEBAR_MODE_KEY, next);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const saved = localStorage.getItem('userInfo');
        if (!saved) throw new Error('No user info found');
        const user = JSON.parse(saved);

        // Skip permission API entirely if no departmentId provided (e.g. Pharmacy)
        // or if user has full access role
        if (departmentId == null || FULL_ACCESS_ROLES.includes(user.role)) {
          if (!cancelled) setAllowedKeys(null);
          return;
        }

        try {
          const res = await axios.post(getUserDepartmentComponents, {
            roleId: user.role,
            departmentId,
          });
          if (cancelled) return;
          if (res.data?.success && Array.isArray(res.data.components)) {
            const allowed = res.data.components
              .filter((c) => c.permissions && c.permissions.access === true)
              .map((c) => c.component_name);
            setAllowedKeys(allowed.length ? allowed : null);
          } else {
            setAllowedKeys(null);
          }
        } catch {
          setAllowedKeys(null);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load department');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [departmentId]);

  const visibleItems = useMemo(() => {
    if (!allowedKeys) return menuItems;
    return menuItems.filter((it) => allowedKeys.includes(it.key));
  }, [menuItems, allowedKeys]);

  useEffect(() => {
    if (activeTab >= visibleItems.length && visibleItems.length > 0) {
      setActiveTab(0);
    }
  }, [visibleItems.length, activeTab]);

  const activeItem = visibleItems[activeTab];
  const ActiveComponent = activeItem?.component;

  const handleSelect = (idx) => {
    setActiveTab(idx);
    if (isMobile) setMobileOpen(false);
  };

  // Cross-tab navigation API exposed to render functions
  const navigateTo = (key) => {
    const idx = visibleItems.findIndex((it) => it.key === key);
    if (idx >= 0) setActiveTab(idx);
  };
  const renderCtx = { navigate: navigateTo };

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={42} sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="error" sx={{ maxWidth: 420 }}>{error}</Alert>
      </Box>
    );
  }

  // ── Sidebar content (used in both desktop floating panel and mobile drawer) ──
  const renderSidebar = ({ expanded, mobile = false }) => (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.paper',
      fontFamily: '"Inter","Segoe UI","Helvetica Neue",Arial,sans-serif',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{
        px: expanded ? 2.25 : 1,
        py: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        minHeight: 64,
      }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.75,
          bgcolor: `${PRIMARY}14`,
          color: PRIMARY,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {departmentIcon}
        </Box>

        {/* Department name — fades out when collapsed */}
        <Box sx={{
          flex: 1,
          minWidth: 0,
          opacity: expanded ? 1 : 0,
          maxWidth: expanded ? 200 : 0,
          overflow: 'hidden',
          transition: `opacity 0.18s ${TRANSITION}, max-width 0.28s ${TRANSITION}`,
        }}>
          <Typography sx={{
            fontSize: '0.62rem', fontWeight: 700,
            letterSpacing: '1.2px', textTransform: 'uppercase',
            color: 'text.secondary', lineHeight: 1.2, mb: 0.25,
            whiteSpace: 'nowrap',
          }}>
            Department
          </Typography>
          <Typography sx={{
            fontWeight: 700, color: 'text.primary',
            fontSize: '0.92rem', lineHeight: 1.25,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {departmentName}
          </Typography>
        </Box>

        {/* Pin / unpin toggle — desktop only */}
        {!mobile && expanded && (
          <Tooltip title={isFixed ? 'Unpin (collapses on mouse leave)' : 'Pin sidebar open'} arrow>
            <IconButton
              size="small"
              onClick={toggleMode}
              sx={{
                color: isFixed ? PRIMARY : 'text.secondary',
                bgcolor: isFixed ? `${PRIMARY}14` : 'transparent',
                '&:hover': { bgcolor: `${PRIMARY}1F`, color: PRIMARY },
                width: 30, height: 30,
                flexShrink: 0,
              }}
            >
              {isFixed
                ? <PinIcon sx={{ fontSize: 17 }} />
                : <PinOutlinedIcon sx={{ fontSize: 17 }} />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Menu */}
      <Box sx={{ flexGrow: 1, py: 1.5, overflowY: 'auto' }}>
        {expanded && (
          <Typography sx={{
            px: 3, mb: 0.5,
            fontSize: '0.62rem', fontWeight: 700,
            color: 'text.secondary', letterSpacing: '1px', textTransform: 'uppercase',
            opacity: expanded ? 1 : 0,
            transition: `opacity 0.18s ${TRANSITION}`,
          }}>
            Navigation
          </Typography>
        )}

        <List sx={{ px: expanded ? 1.25 : 0.75, py: 0 }}>
          {visibleItems.length === 0 ? (
            expanded && (
              <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                  No sections available for your role.
                </Typography>
              </Box>
            )
          ) : (
            visibleItems.map((item, idx) => {
              // Section heading: render once whenever the `section` value
              // changes vs the previous item. Hidden when the sidebar is
              // collapsed to a rail (no labels visible at all there); a
              // thin divider line takes its place so the groups still read.
              const prevSection = idx > 0 ? visibleItems[idx - 1].section : null;
              const showHeading = item.section && item.section !== prevSection;
              const isSelected = idx === activeTab;
              const badgeCount = Number(item.badge || 0);
              const hasBadge   = badgeCount > 0;

              // Inline pill (expanded sidebar): bell + count
              const expandedBadge = hasBadge && (
                <Box sx={{
                  ml: 0.75,
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  px: 0.9, py: 0.25, borderRadius: 999,
                  bgcolor: '#fef2f2',
                  color: '#b91c1c',
                  fontSize: '0.82rem', fontWeight: 800,
                  lineHeight: 1,
                  border: '1px solid #fecaca',
                  flexShrink: 0,
                  // Gentle pulse to draw the eye
                  animation: 'tcReqPulse 1.8s ease-in-out infinite',
                  '@keyframes tcReqPulse': {
                    '0%, 100%': { boxShadow: '0 0 0 0 rgba(220,38,38,0.35)' },
                    '50%':      { boxShadow: '0 0 0 4px rgba(220,38,38,0)' },
                  },
                }}>
                  <BellIcon sx={{ fontSize: 14 }} />
                  {badgeCount}
                </Box>
              );

              // Floating dot+count overlay (collapsed rail)
              const collapsedBadge = hasBadge && (
                <Box sx={{
                  position: 'absolute',
                  top: 3, right: 6,
                  minWidth: 18, height: 18,
                  px: 0.5, borderRadius: 999,
                  bgcolor: '#dc2626', color: '#fff',
                  fontSize: '0.72rem', fontWeight: 800,
                  lineHeight: 1,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 0 0 2px #fff',
                  animation: 'tcReqPulse 1.8s ease-in-out infinite',
                  '@keyframes tcReqPulse': {
                    '0%, 100%': { boxShadow: '0 0 0 2px #fff, 0 0 0 0 rgba(220,38,38,0.45)' },
                    '50%':      { boxShadow: '0 0 0 2px #fff, 0 0 0 5px rgba(220,38,38,0)' },
                  },
                }}>
                  {badgeCount > 99 ? '99+' : badgeCount}
                </Box>
              );

              const button = (
                <ListItemButton
                  selected={isSelected}
                  onClick={() => handleSelect(idx)}
                  sx={{
                    position: 'relative',
                    borderRadius: 1.5,
                    mb: 0.25,
                    py: expanded ? 1 : 1.25,
                    px: expanded ? 1.5 : 0,
                    minHeight: 42,
                    justifyContent: expanded ? 'flex-start' : 'center',
                    transition: `padding 0.2s ${TRANSITION}, background-color .15s ease`,
                    '&:hover': { bgcolor: 'action.hover' },
                    '&.Mui-selected': {
                      bgcolor: `${PRIMARY}14`,
                      '&:hover': { bgcolor: `${PRIMARY}1F` },
                      '& .MuiListItemIcon-root': { color: PRIMARY },
                      '& .MuiListItemText-primary': { color: PRIMARY, fontWeight: 600 },
                    },
                  }}
                >
                  <ListItemIcon sx={{
                    minWidth: expanded ? 32 : 0,
                    color: hasBadge ? '#dc2626' : 'text.secondary',
                    justifyContent: 'center',
                  }}>
                    {React.cloneElement(item.icon, { sx: { fontSize: 20 } })}
                  </ListItemIcon>
                  <Box sx={{
                    flex: 1, minWidth: 0,
                    display: 'flex', alignItems: 'center',
                    opacity: expanded ? 1 : 0,
                    maxWidth: expanded ? 180 : 0,
                    overflow: 'hidden',
                    transition: `opacity 0.18s ${TRANSITION}, max-width 0.28s ${TRANSITION}`,
                  }}>
                    <Typography sx={{
                      fontSize: '0.9rem',
                      fontWeight: hasBadge ? 600 : 500,
                      color: 'text.primary',
                      whiteSpace: 'nowrap',
                    }}>
                      {item.label}
                    </Typography>
                    {expandedBadge}
                  </Box>
                  {!expanded && !mobile && collapsedBadge}
                </ListItemButton>
              );

              const tooltipTitle = hasBadge
                ? `${item.label} — ${badgeCount} pending`
                : item.label;

              return (
                <Box key={item.key}>
                  {/* Section heading. Shown when the sidebar is expanded —
                      otherwise we drop in a thin separator line so the
                      grouping is still implied on the collapsed rail. */}
                  {showHeading && (
                    expanded ? (
                      <Typography sx={{
                        px: 1.5,
                        mt: idx === 0 ? 0 : 1.5,
                        mb: 0.5,
                        fontSize: '0.6rem',
                        fontWeight: 800,
                        color: 'text.secondary',
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        userSelect: 'none',
                      }}>
                        {item.section}
                      </Typography>
                    ) : (
                      idx > 0 && (
                        <Box sx={{
                          height: 1, mx: 1.25, my: 0.5,
                          bgcolor: 'divider',
                        }} />
                      )
                    )
                  )}
                  {!expanded && !mobile ? (
                    <Tooltip title={tooltipTitle} placement="right" arrow>
                      {button}
                    </Tooltip>
                  ) : button}
                </Box>
              );
            })
          )}
        </List>
      </Box>
    </Box>
  );

  const sidebarW = isExpanded ? DRAWER_WIDTH : RAIL_WIDTH;

  return (
    <Box sx={{
      display: 'flex',
      gap: { xs: 1.5, md: 2.5 },
      alignItems: 'flex-start',
      minHeight: 'calc(100vh - 96px)',
      width: '100%',
    }}>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
        }}
      >
        {renderSidebar({ expanded: true, mobile: true })}
      </Drawer>

      {/* Desktop sidebar — collapsible card */}
      <Paper
        elevation={0}
        onMouseEnter={() => { if (!isFixed) setHovering(true); }}
        onMouseLeave={() => { if (!isFixed) setHovering(false); }}
        sx={{
          display: { xs: 'none', md: 'block' },
          width: sidebarW,
          flexShrink: 0,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          height: 'calc(100vh - 96px)',
          position: 'sticky',
          top: 80,
          overflow: 'hidden',
          transition: `width 0.28s ${TRANSITION}, box-shadow 0.2s ease`,
          boxShadow: !isFixed && hovering
            ? '0 8px 32px rgba(15,23,42,0.10)'
            : '0 1px 2px rgba(15,23,42,0.04)',
          // keep the floating expanded panel above content
          zIndex: !isFixed && hovering ? 5 : 1,
        }}
      >
        {renderSidebar({ expanded: isExpanded, mobile: false })}
      </Paper>

      {/* Main content */}
      <Box sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}>
        {/* Mobile top bar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            display: { xs: 'block', md: 'none' },
            bgcolor: 'background.paper',
            color: 'text.primary',
            border: '1px solid',
          borderColor: 'divider',
            borderRadius: 1.5,
            mb: 1.5,
          }}
        >
          <Toolbar sx={{ minHeight: 56 }}>
            <IconButton
              edge="start"
              onClick={() => setMobileOpen(true)}
              aria-label="open department menu"
              sx={{ mr: 1.5, color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton>
            <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>
              {activeItem?.label || departmentName}
            </Typography>
          </Toolbar>
        </AppBar>

        <Box sx={{ flexGrow: 1, width: '100%', minWidth: 0 }}>
          {activeItem?.render ? (
            activeItem.render(renderCtx)
          ) : ActiveComponent ? (
            <ActiveComponent />
          ) : (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography sx={{ color: 'text.secondary' }}>
                Select a section from the sidebar to begin.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
